import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, User, Thermometer, MapPin, Moon, Bell, Save, Loader2,
  Tractor, ExternalLink, Cloud, Sun
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserSettings() {
  const { user, login } = useAuth();
  const { theme, toggleTheme, sunTimes, isDaytime, manualOverride, formatSunTime } = useTheme();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [preferences, setPreferences] = useState({
    temp_unit: user?.preferences?.temp_unit || "fahrenheit",
    notifications_enabled: user?.preferences?.notifications_enabled || false,
    default_location: user?.preferences?.default_location || null,
    forecast_source_preference: user?.preferences?.forecast_source_preference || "blend"
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      if (name !== user?.name) {
        await axios.put(`${API}/user/profile?name=${encodeURIComponent(name)}`);
      }
      await axios.put(`${API}/user/preferences`, preferences);
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-8">
      {/* Header */}
      <header className="flex items-center gap-4 mb-8">
        <Link to="/">
          <Button variant="ghost" size="icon" className="rounded-xl hover:bg-white/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
            <User className="w-6 h-6 text-[#00f2ea]" />
            Settings
          </h1>
          <p className="text-white/50 text-sm">Manage your preferences</p>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl space-y-6"
      >
        {/* Profile Section */}
        <div className="glass rounded-2xl p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-[#00f2ea]" />
            Profile
          </h3>
          
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-white/10 rounded-xl h-12"
              data-testid="settings-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              value={user?.email || ""}
              disabled
              className="bg-white/5 border-white/10 rounded-xl h-12 opacity-60"
            />
            <p className="text-xs text-white/40">Email cannot be changed</p>
          </div>
        </div>

        {/* Preferences Section */}
        <div className="glass rounded-2xl p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-[#ff0080]" />
            Preferences
          </h3>

          <div className="space-y-2">
            <Label>Temperature Unit</Label>
            <Select
              value={preferences.temp_unit}
              onValueChange={(value) => setPreferences({ ...preferences, temp_unit: value })}
            >
              <SelectTrigger 
                className="bg-white/5 border-white/10 rounded-xl h-12"
                data-testid="settings-temp-unit"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                <SelectItem value="celsius">Celsius (°C)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Forecast Source Preference</Label>
            <Select
              value={preferences.forecast_source_preference}
              onValueChange={(value) => setPreferences({ ...preferences, forecast_source_preference: value })}
            >
              <SelectTrigger
                className="bg-white/5 border-white/10 rounded-xl h-12"
                data-testid="settings-forecast-source-preference"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blend">TheForecastTracker Blend</SelectItem>
                <SelectItem value="openweather">OpenWeatherMap</SelectItem>
                <SelectItem value="nws">National Weather Service</SelectItem>
                <SelectItem value="open_meteo">Open-Meteo</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-white/40" data-testid="settings-forecast-source-help-text">
              This controls which forecast source you prefer for detailed comparison cards. You can change it anytime.
            </p>
          </div>

          <div className="space-y-3">
            <Label>Theme</Label>
            <div className="p-4 bg-white/5 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isDaytime ? (
                    <Sun className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <Moon className="w-5 h-5 text-blue-400" />
                  )}
                  <div>
                    <p className="font-medium">
                      {isDaytime ? "Daytime" : "Nighttime"}
                    </p>
                    <p className="text-xs text-white/50">
                      {isDaytime 
                        ? `Light mode until ${formatSunTime(sunTimes.sunset)}`
                        : `Dark mode until ${formatSunTime(sunTimes.sunrise)}`
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleTheme}
                  className="gap-2"
                  data-testid="theme-toggle-btn"
                >
                  {theme === "light" ? (
                    <>
                      <Moon className="w-4 h-4" />
                      Switch to Dark
                    </>
                  ) : (
                    <>
                      <Sun className="w-4 h-4" />
                      Switch to Light
                    </>
                  )}
                </Button>
              </div>
              {manualOverride && (
                <p className="text-xs text-[#ccff00]">
                  Theme manually overridden. Will reset at {isDaytime ? "sunset" : "sunrise"}.
                </p>
              )}
              <div className="pt-2 border-t border-white/10">
                <p className="text-xs text-white/40">
                  Sunrise: {formatSunTime(sunTimes.sunrise)} • Sunset: {formatSunTime(sunTimes.sunset)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#ccff00]" />
                Weather Notifications
              </Label>
              <p className="text-sm text-white/50">Receive alerts for severe weather</p>
            </div>
            <Switch
              checked={preferences.notifications_enabled}
              onCheckedChange={(checked) => setPreferences({ ...preferences, notifications_enabled: checked })}
              data-testid="settings-notifications"
            />
          </div>
        </div>

        {/* Default Location Section */}
        <div className="glass rounded-2xl p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#7000ff]" />
            Default Location
          </h3>
          
          {preferences.default_location ? (
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#00f2ea]" />
                <span>{preferences.default_location.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreferences({ ...preferences, default_location: null })}
                className="text-red-400 hover:bg-red-500/20"
              >
                Remove
              </Button>
            </div>
          ) : (
            <p className="text-white/50 text-sm">
              No default location set. Your current location will be detected automatically.
            </p>
          )}
          <p className="text-xs text-white/40">
            Tip: To set a default location, save a location from the dashboard and it will appear here.
          </p>
        </div>

        {/* Our Other Apps Section */}
        <div className="glass rounded-2xl p-6 space-y-6">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-[#ccff00]" />
            Our Other Apps
          </h3>
          
          <a 
            href="https://thefarmtracker.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#ccff00]/10">
                <Tractor className="w-6 h-6 text-[#ccff00]" />
              </div>
              <div>
                <h4 className="font-semibold">TheFarmTracker</h4>
                <p className="text-sm text-white/50">Track your farm operations, livestock, and crops</p>
              </div>
            </div>
            <ExternalLink className="w-5 h-5 text-white/40 group-hover:text-[#ccff00] transition-colors" />
          </a>
          
          <a 
            href="https://theforecasttracker.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#00f2ea]/10">
                <Cloud className="w-6 h-6 text-[#00f2ea]" />
              </div>
              <div>
                <h4 className="font-semibold">TheForecastTracker</h4>
                <p className="text-sm text-white/50">Advanced weather tracking and forecasts</p>
              </div>
            </div>
            <ExternalLink className="w-5 h-5 text-white/40 group-hover:text-[#00f2ea] transition-colors" />
          </a>
        </div>

        {/* Save Button */}
        <Button 
          onClick={handleSaveProfile}
          disabled={saving}
          className="w-full h-12 bg-white text-black hover:bg-white/90 rounded-xl font-semibold"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          Save Settings
        </Button>
      </motion.div>
    </div>
  );
}
